import { useEffect, useMemo, useRef, useState } from "react";
import {
	data,
	Outlet,
	useLoaderData,
	useNavigate,
	useNavigation,
	type LoaderFunctionArgs,
} from "react-router";

import { isPageFontLoaded, loadPageFont } from "@/lib/fonts";
import { juzForPage } from "@/lib/juz";
import { usePageTurnDirection } from "@/lib/pageTurn";
import { isValidPage, loadPage, prefetchAround } from "@/lib/pages";
import { surahFromAudioUrl } from "@/lib/utils";
import { useLastPage } from "@/store/settings";
import { groupWordsToLines } from "@/utils";

import QuranPageNav from "@/components/QuranPageNav";
import QuranPageReadingView from "@/components/QuranPageReadingView";
import QuranPagePagination from "@/components/QuranPagePagination";
import QuranPageSkeleton from "@/components/QuranPageSkeleton";

import { PAGE_COUNT, type PageData } from "@/lib/types";

export function quranPageLoader({ params }: LoaderFunctionArgs) {
	const page = Number(params.pageNumber);
	if (!isValidPage(page)) throw data("Not found", { status: 404 });
	return loadPage(page);
}

// The loader normally resolves only after the font is ready; this is the
// fallback for when that load failed or is still in flight.
function usePageFont(page: number) {
	const [loadedPage, setLoadedPage] = useState(() => (isPageFontLoaded(page) ? page : 0));

	useEffect(() => {
		let active = true;
		if (isPageFontLoaded(page)) {
			setLoadedPage(page);
			return;
		}
		loadPageFont(page)
			.then(() => {
				if (active) setLoadedPage(page);
			})
			.catch(console.error);
		return () => {
			active = false;
		};
	}, [page]);

	// Checked synchronously: the component is reused across pages, so state from the
	// previous page must not render a skeleton frame into the page turn.
	return loadedPage === page || isPageFontLoaded(page);
}

/** ← / → keys and horizontal swipes turn pages (the mushaf reads right-to-left). */
function usePageTurns(page: number) {
	const navigate = useNavigate();
	const navigation = useNavigation();
	// While a page is still loading, turn from that page rather than the one on
	// screen, so quick presses add up (51 -> 53) instead of re-requesting 52.
	const pending = Number(navigation.location?.pathname.slice(1));
	const from = useRef(page);
	from.current = isValidPage(pending) ? pending : page;
	const busy = useRef(false);
	busy.current = navigation.state !== "idle";

	useEffect(() => {
		const go = (delta: number, animate = true) => {
			const target = from.current + delta;
			if (target >= 1 && target <= PAGE_COUNT) navigate(`/${target}`, { viewTransition: animate });
		};
		const blocked = (target: EventTarget | null) =>
			document.querySelector('[role="dialog"]') ||
			(target instanceof HTMLElement && target.closest("input, textarea, select"));

		const onKey = (e: KeyboardEvent) => {
			if (e.altKey || e.ctrlKey || e.metaKey || blocked(e.target)) return;
			if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
			// Holding the key skims: no animation, and never abort a page that is
			// still loading (re-navigating would restart its fetch forever).
			if (e.repeat && busy.current) return;
			go(e.key === "ArrowLeft" ? 1 : -1, !e.repeat);
		};

		let start: { x: number; y: number } | null = null;
		const onTouchStart = (e: TouchEvent) => {
			start = e.touches.length === 1 ? { x: e.touches[0].clientX, y: e.touches[0].clientY } : null;
		};
		const onTouchEnd = (e: TouchEvent) => {
			if (!start || blocked(e.target)) return;
			const dx = e.changedTouches[0].clientX - start.x;
			const dy = e.changedTouches[0].clientY - start.y;
			start = null;
			if (Math.abs(dx) < 70 || Math.abs(dy) > Math.abs(dx) / 2) return;
			go(dx > 0 ? 1 : -1);
		};

		window.addEventListener("keydown", onKey);
		window.addEventListener("touchstart", onTouchStart, { passive: true });
		window.addEventListener("touchend", onTouchEnd, { passive: true });
		return () => {
			window.removeEventListener("keydown", onKey);
			window.removeEventListener("touchstart", onTouchStart);
			window.removeEventListener("touchend", onTouchEnd);
		};
	}, [navigate]);
}

/** Browser chrome (theme-color) matches the cream page while the reader is open. */
export function useReaderThemeColor() {
	useEffect(() => {
		const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
		if (!meta) return;
		const previous = meta.content;
		meta.content = "#faf5ea";
		return () => {
			meta.content = previous;
		};
	}, []);
}

export default function QuranPage() {
	const { pageNumber, ayahs, video, editMode } = useLoaderData<PageData>();
	const fontLoaded = usePageFont(pageNumber);
	const { setLastPage } = useLastPage();
	usePageTurns(pageNumber);
	usePageTurnDirection(pageNumber);
	useReaderThemeColor();

	useEffect(() => {
		prefetchAround(pageNumber);
		setLastPage(pageNumber);
		document.title = `Stranica ${pageNumber} · Hifz Academy`;
		window.scrollTo(0, 0);
	}, [pageNumber, setLastPage]);

	const lines = useMemo(() => {
		const words = ayahs.flatMap((ayah) => ayah.words);
		words.sort((a, b) => a.line_number - b.line_number || a.id - b.id);
		return groupWordsToLines(words);
	}, [ayahs]);

	// The surah the page opens in: from the first word that has audio (ayah-end
	// markers have none), in reading order.
	const surahNumber = useMemo(() => {
		for (const line of lines) {
			for (const word of line) {
				const surah = surahFromAudioUrl(word.audio_url);
				if (surah) return surah;
			}
		}
		return null;
	}, [lines]);

	return (
		<main className="flex min-h-svh flex-col bg-paper-100 md:bg-paper-200 print:min-h-0 print:bg-white">
			<QuranPageNav
				video={video}
				pageNumber={pageNumber}
				surahNumber={surahNumber}
				juz={juzForPage(pageNumber)}
			/>
			<section className="mushaf-scope w-full flex-1 px-3 py-4 md:py-8 [touch-action:pan-y_pinch-zoom]">
				{fontLoaded ? (
					<QuranPageReadingView
						key={pageNumber}
						lines={lines}
						ayahs={ayahs}
						editMode={editMode}
						pageNumber={pageNumber}
					/>
				) : (
					<QuranPageSkeleton pageNumber={pageNumber} />
				)}
			</section>
			<QuranPagePagination pageNumber={pageNumber} />
			<Outlet />
		</main>
	);
}
